import { css } from '@emotion/react'
import gql from 'graphql-tag'
import get from 'lodash/get'
import React, { useCallback, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link } from 'react-router-dom'
import toastr from 'toastr'
import usePaginatedQuery from '../../api/usePaginatedQuery'
import { EventDelete } from '../../graphql/eventDelete'
import { submittedEventFragment } from '../../graphql/fragments'
import useRecordSubmissionMutation from '../../hooks/useRecordSubmissionMutation'
import { Event } from '../../models'
import useUserOrganizations from '../../hooks/useUserOrganizations'
import Routes from '../../startup/routes'
import { YourEventsTabScope } from '../../util/constants'
import EventSearchField from '../components/dashboard/yourEvents/EventSearchField'
import InternalOnlyFilter, { InternalOnlyFilterValue } from '../components/dashboard/yourEvents/InternalOnlyFilter'
import SubmittedAsFilter, { SubmittedAsFilterValue } from '../components/dashboard/yourEvents/SubmittedAsFilter'
import YourEventsTabContent from '../components/dashboard/yourEvents/YourEventsTabContent'
import YourEventsTabHeader from '../components/dashboard/yourEvents/YourEventsTabHeader'

export const YourPastEventsQuery = gql`
  ${submittedEventFragment}
  query YourPastEventsQuery($cursor: String, $internalOnly: Boolean, $submittedAs: String) {
    currentUser {
      id
      submittedPastEvents(first: 30, after: $cursor, internalOnly: $internalOnly, submittedAs: $submittedAs) {
        nodes {
          ...submittedEventFragment
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`

const YourPastEventsContainer: React.FC = () => {
  const [internalOnlyFilter, setInternalOnlyFilter] = useState<InternalOnlyFilterValue>('all')
  const [submittedAsFilter, setSubmittedAsFilter] = useState<SubmittedAsFilterValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const { organizations } = useUserOrganizations()
  const hasActiveSubscription = useMemo(
    () => organizations.some(org => org.hasActiveSubscription),
    [organizations]
  )

  // Convert filter value to GraphQL variable
  const internalOnlyVar = useMemo(() => {
    if (internalOnlyFilter === 'internal') return true
    if (internalOnlyFilter === 'network') return false
    return undefined // 'all' means no filter
  }, [internalOnlyFilter])

  const submittedAsVar = useMemo(() => {
    if (submittedAsFilter === 'all') return undefined
    return submittedAsFilter
  }, [submittedAsFilter])

  const variables = useMemo(() => ({
    ...(internalOnlyVar !== undefined && { internalOnly: internalOnlyVar }),
    ...(submittedAsVar !== undefined && { submittedAs: submittedAsVar }),
  }), [internalOnlyVar, submittedAsVar])

  const query = YourPastEventsQuery
  const { handleSubmit, mutationLoading } = useRecordSubmissionMutation({
    query: EventDelete,
    queryName: 'eventDelete',
    recordName: 'event',
    onSuccess: () => toastr.success('Event removed.'),
    submitOptions: { refetchQueries: () => [{ query, variables }] },
  })

  const removeRecord = useCallback(recordId => handleSubmit({ id: recordId }), [handleSubmit])

  const { data, loading, fetchNextPage, hasNextPage } = usePaginatedQuery(query, 'currentUser.submittedPastEvents', {
    variables,
  })
  const allRecords = get(data, 'currentUser.submittedPastEvents.nodes', [])

  // Client-side search filter
  const records = useMemo(() => {
    if (!searchQuery.trim()) return allRecords
    const lowerSearch = searchQuery.toLowerCase()
    return allRecords.filter((event: Event) => event.title?.toLowerCase().includes(lowerSearch))
  }, [allRecords, searchQuery])

  const isLoading = loading || mutationLoading

  return (
    <section className="dash-your-events">
      <Helmet>
        <title>Your Events</title>
      </Helmet>
      <header className="dash-section-greeting">
        <h1 className="dash-greeting-heading">Your Events</h1>
        <Link to={Routes.newEvent()} className="btn btn-dash-add-event">
          Submit an Event
        </Link>
      </header>

      <YourEventsTabHeader activeTabScope={YourEventsTabScope.PAST} />
      <div
        css={css`
          padding: 16px 30px 0;
          border: 1px solid #d0d0d0;
          border-radius: 0 10px 10px 10px;
          background: #fbfbfb;
        `}
      >
        <div css={css`display: flex; align-items: center; gap: 16px; flex-wrap: wrap; margin-bottom: 16px;`}>
          <InternalOnlyFilter
            value={internalOnlyFilter}
            onChange={setInternalOnlyFilter}
            hasActiveSubscription={hasActiveSubscription}
          />
          <SubmittedAsFilter value={submittedAsFilter} onChange={setSubmittedAsFilter} />
          <EventSearchField value={searchQuery} onChange={setSearchQuery} />
        </div>
        <YourEventsTabContent
          events={records}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          removeRecord={removeRecord}
          isLoading={isLoading}
          tabScope={YourEventsTabScope.PAST}
        />
      </div>
    </section>
  )
}

export default YourPastEventsContainer
